import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ErrorAlert from '../components/ErrorAlert';
import PasskeyLoginButton from '../components/PasskeyLoginButton';
import RecoverAdminModal from '../components/RecoverAdminModal';

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberUsername, setRememberUsername] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const navigate = useNavigate();

  // Load saved username on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedAdminEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberUsername(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Enable cookies
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const errorData = await res.json();
        setError(errorData.detail || 'Admin login failed. Please check your credentials.');
        return;
      }

      const data = await res.json();

      // Reject anyone who isn't an admin or king (the admin-login
      // page is only the admin/king entry point — readers/writers
      // get the same `auth/login` endpoint but via their own page).
      const role = data.role || '';
      if (role !== 'admin' && role !== 'coadmin' && role !== 'king') {
        setError('This account does not have admin or king access.');
        return;
      }

      // Save or remove username based on checkbox
      if (rememberUsername) {
        localStorage.setItem('rememberedAdminEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedAdminEmail');
      }

      localStorage.setItem('userRole', role);
      localStorage.setItem('userEmail', data.email);
      // Session token is now in httpOnly cookie. Land on the reshaped
      // /admin-panel — Feb 28 2026 user request: button-grid landing.
      navigate('/admin-panel');
    } catch (error) {
      console.error('Admin login error:', error);
      setError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600/20 rounded-full mb-4">
            <Shield className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">High King Access</h1>
          <p className="text-gray-400 text-sm">Restricted area - authorized personnel only</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-red-900/50">
          <ErrorAlert message={error} onClose={() => setError('')} />
          <PasskeyLoginButton
            defaultEmail={formData.email}
            onSuccess={(u) => {
              if (u.role !== 'admin' && u.role !== 'coadmin' && u.role !== 'king') {
                setError('Passkey belongs to a non-admin account.');
                return;
              }
              localStorage.setItem('userRole', u.role);
              localStorage.setItem('userEmail', u.email);
              navigate('/admin-panel');
            }}
            testId="admin-passkey-login-btn"
          />
          <div className="flex items-center my-5">
            <div className="flex-1 h-px bg-slate-700" />
            <span className="px-3 text-xs text-gray-500 uppercase tracking-wider">or password</span>
            <div className="flex-1 h-px bg-slate-700" />
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                placeholder="Admin Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-red-900/50 text-white"
                required
              />
            </div>
            
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full pl-12 pr-12 py-6 bg-slate-900/50 border-red-900/50 text-white"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberUsername"
                checked={rememberUsername}
                onChange={(e) => setRememberUsername(e.target.checked)}
                className="w-4 h-4 rounded border-red-900/50 bg-slate-900/50 text-red-600 focus:ring-red-600 focus:ring-offset-0"
              />
              <label htmlFor="rememberUsername" className="ml-2 text-sm text-gray-300 cursor-pointer">
                Remember username
              </label>
            </div>

            <Button
              type="submit"
              className="w-full py-6 bg-red-600 hover:bg-red-700"
            >
              Access High King Panel
            </Button>
          </form>

          <div className="mt-4 text-center">
            <a 
              href="/forgot-password" 
              className="text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              Forgot Password?
            </a>
          </div>

          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => setRecoverOpen(true)}
              className="text-xs text-gray-500 hover:text-red-300 transition-colors underline underline-offset-4"
              data-testid="recover-admin-link"
            >
              Locked out? Recover Admin
            </button>
          </div>

          <div className="mt-6 text-center">
            <a href="/login" className="text-sm text-gray-400 hover:text-white transition-colors">
              ← Back to Reader Login
            </a>
          </div>
        </div>
      </div>
      <RecoverAdminModal open={recoverOpen} onOpenChange={setRecoverOpen} />
    </div>
  );
};

export default AdminLogin;